"use client";

import type { Ref } from "react";
import { useImperativeHandle, useCallback, useEffect, useRef, useState, useTransition } from "react";

const POLL_INTERVAL = 9000;

const schedTimeout = (interval: number) => new Promise(res => setTimeout(res, interval));

const usePoll = (cb: () => void, interval: number) => {
    useEffect(() => {
        if (!cb) {
            return;
        }

        const ac = new AbortController();
        const { signal } = ac;

        (async () => {
            while (!signal.aborted) {
                cb();
                await schedTimeout(interval);
            }
        })();

        return () => ac.abort();
    }, [cb, interval]);
};

export interface CacheHandle {
    invalidate(): void;
}

const useActionCache: <T,>(ref: Ref<CacheHandle>, cb?: () => Promise<T>) => [T | null, boolean] = <T,>(
    ref: Ref<CacheHandle>,
    cb?: () => Promise<T>
) => {
    const [isPending, startTransition] = useTransition();
    const [state, setState] = useState<T | null>(null);
    useImperativeHandle(ref, () => ({
        invalidate() {
            startTransition(async () => {
                if (!cb) {
                    return;
                }
                setState(await cb());
            });
        }
    }), [cb]);
    return [state, isPending];
};

const useActionPoll: <T>(ref: Ref<CacheHandle>, cb?: () => Promise<T>, interval?: number) => [T | null, boolean] = <T,>(
    ref: Ref<CacheHandle>,
    cb?: () => Promise<T>,
    interval: number = POLL_INTERVAL
) => {
    const myRef = useRef<CacheHandle>(null);
    const [state, isPending] = useActionCache(myRef, cb);
    // FIXME... a mess
    useImperativeHandle(ref, () => ({
        invalidate() {
            myRef.current!.invalidate();
        }
    }), []);

    const poll = useCallback(() => myRef.current!.invalidate(), []);
    usePoll(poll, interval);
    return [state, isPending];
};


export default useActionPoll;
